import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter
} from 'firebase/firestore'
import { db } from './firebase'
import { acceptExplainVisual } from './explainVisuals'

// Every read in this module is bounded. `isLinkedParent` in firestore.rules
// resolves the student profile with a get(), so an unbounded listing of a
// child's sessions would multiply both document reads and rules evaluations.
// One page is 20 documents plus a single rules lookup; "Load more" pages the
// rest on demand.
export const EXPLAIN_SESSION_PAGE_SIZE = 20

// Mirrors EXPLAIN_MAX_CHAT_MESSAGES in functions/index.js: askExplainQuestion
// refuses to write past this, so a session can never hold more.
export const EXPLAIN_CHAT_MESSAGE_LIMIT = 120

const EXPLAIN_STRUGGLE_PROFILE_DOC_ID = 'current'

const EXPLAIN_STATUS_LABELS = {
  'in-progress': 'In progress',
  completed: 'Completed',
  abandoned: 'Stopped'
}

const asString = (value) => (typeof value === 'string' ? value : '')

const explainSessionsRef = (userId) =>
  collection(db, 'users', userId, 'explainSessions')

/**
 * One step as every Explain surface renders it. Shared by the stored-session
 * path below and the fresh analyze result in StudyRoom, so a figure reaches
 * the renderer through the same guard either way.
 */
export const normalizeExplainStep = (step) => ({
  solution: asString(step?.solution),
  explanation: asString(step?.explanation),
  visual: acceptExplainVisual(step?.visual)
})

/**
 * Shapes a raw session document into the object both the Study Room and the
 * Parent Dashboard render, so neither surface has to defend against a
 * half-written or legacy document on every access.
 */
export const normalizeExplainSession = (id, data = {}) => {
  const rawQuestion = data.question && typeof data.question === 'object'
    ? data.question
    : {}
  const steps = Array.isArray(data.steps)
    ? data.steps.map(normalizeExplainStep)
    : []
  const rawIndex = Number(data.currentStepIndex)
  const currentStepIndex = Number.isInteger(rawIndex) && rawIndex >= 0
    ? Math.min(rawIndex, Math.max(steps.length - 1, 0))
    : 0

  return {
    id,
    question: {
      text: asString(rawQuestion.text),
      latex: asString(rawQuestion.latex),
      mathml: asString(rawQuestion.mathml),
      confidence: Number.isFinite(Number(rawQuestion.confidence))
        ? Number(rawQuestion.confidence)
        : 0
    },
    steps,
    finalAnswer: asString(data.finalAnswer),
    finalVisual: acceptExplainVisual(data.finalVisual),
    topic: asString(data.topic) || null,
    mathBranch: asString(data.mathBranch) || null,
    difficulty: asString(data.difficulty) || null,
    questionHash: asString(data.questionHash) || null,
    status: EXPLAIN_STATUS_LABELS[data.status] ? data.status : 'in-progress',
    currentStepIndex,
    // Narration is opt-in: only an explicit `true` counts as unmuted.
    ttsEnabled: data.ttsEnabled === true,
    chatMessageCount: Number(data.chatMessageCount) || 0,
    xpAwarded: data.xpAwarded === true,
    xpAmount: Number(data.xpAmount) || 0,
    diagnosisId: asString(data.diagnosisId) || null,
    diagnosisWeekId: asString(data.diagnosisWeekId) || null,
    createdAt: data.createdAt || null,
    updatedAt: data.updatedAt || null,
    completedAt: data.completedAt || null
  }
}

/**
 * Newest-first page of a student's explain sessions.
 *
 * Ordering on `createdAt` alone is served by the automatic single-field index,
 * so no composite index is required. Filtering by status here would need one —
 * the surfaces label status instead.
 *
 * @param {string} userId - Student whose sessions to read.
 * @param {{pageSize?: number, cursor?: object}} options - Page size and the
 *   document snapshot returned as `cursor` by the previous page.
 * @return {Promise<{sessions: Array<object>, cursor: object|null,
 *   hasMore: boolean}>} One page of sessions.
 */
export const fetchExplainSessions = async (userId, options = {}) => {
  const pageSize = options.pageSize || EXPLAIN_SESSION_PAGE_SIZE
  const constraints = [orderBy('createdAt', 'desc')]
  if (options.cursor) {
    constraints.push(startAfter(options.cursor))
  }
  // One extra document answers "is there another page?" without a count query.
  constraints.push(limit(pageSize + 1))

  const snapshot = await getDocs(query(explainSessionsRef(userId), ...constraints))
  const docs = snapshot.docs.slice(0, pageSize)

  return {
    sessions: docs.map(sessionDoc =>
      normalizeExplainSession(sessionDoc.id, sessionDoc.data())),
    cursor: docs.length ? docs[docs.length - 1] : null,
    hasMore: snapshot.docs.length > pageSize
  }
}

/**
 * The chat thread for one session, in the order the messages were written.
 * Loaded only when a session is opened — never alongside a session list.
 *
 * @param {string} userId - Student who owns the session.
 * @param {string} sessionId - Session to read.
 * @return {Promise<Array<object>>} Messages ordered by `sequence`.
 */
export const fetchExplainChat = async (userId, sessionId) => {
  const snapshot = await getDocs(query(
    collection(db, 'users', userId, 'explainSessions', sessionId, 'chat'),
    orderBy('sequence', 'asc'),
    limit(EXPLAIN_CHAT_MESSAGE_LIMIT)
  ))

  return snapshot.docs.map(messageDoc => {
    const data = messageDoc.data()
    return {
      id: messageDoc.id,
      role: data.role === 'assistant' ? 'assistant' : 'user',
      content: asString(data.content),
      stepIndex: Number.isInteger(data.stepIndex) ? data.stepIndex : null,
      sequence: Number(data.sequence) || 0,
      createdAt: data.createdAt || null
    }
  })
}

/**
 * The diagnosis written for a session, if one exists.
 *
 * The session document stores the week and entry ids that
 * diagnoseExplainSession wrote, so this is a single addressed get rather than
 * a scan across weekly diagnosis collections.
 *
 * @param {string} userId - Student who owns the session.
 * @param {object} session - A session from {@link normalizeExplainSession}.
 * @return {Promise<object|null>} The diagnosis entry, or null when the session
 *   has none.
 */
export const fetchExplainDiagnosis = async (userId, session) => {
  if (!session?.diagnosisWeekId || !session?.diagnosisId) return null

  const snapshot = await getDoc(doc(
    db, 'users', userId,
    'diagnoses', session.diagnosisWeekId,
    'entries', session.diagnosisId
  ))
  if (!snapshot.exists()) return null

  const data = snapshot.data()
  return {
    id: snapshot.id,
    diagnosis: asString(data.diagnosis),
    topics: Array.isArray(data.topics)
      ? data.topics.filter(topic => typeof topic === 'string' && topic.trim())
      : [],
    createdAt: data.createdAt || null
  }
}

/**
 * The rolling struggle summary maintained by the rollupStruggleProfile job.
 * A single document read; there is no history to page through.
 *
 * @param {string} userId - Student whose profile to read.
 * @return {Promise<object|null>} The profile, or null when none exists yet.
 */
export const fetchStruggleProfile = async (userId) => {
  const snapshot = await getDoc(doc(
    db, 'users', userId, 'struggleProfile', EXPLAIN_STRUGGLE_PROFILE_DOC_ID
  ))
  if (!snapshot.exists()) return null

  const data = snapshot.data()
  const summary = asString(data.summary)
  if (!summary) return null

  return {
    summary,
    updatedAt: data.updatedAt || null,
    diagnosisCount: Number(data.diagnosisCount) || 0
  }
}

export const explainStatusLabel = (status) =>
  EXPLAIN_STATUS_LABELS[status] || EXPLAIN_STATUS_LABELS['in-progress']

/** A completed session is frozen by the rules; anything else can be resumed. */
export const isExplainSessionResumable = (session) =>
  Boolean(session) && session.status !== 'completed' && session.steps.length > 0

export const explainSessionQuestionText = (session) =>
  session?.question?.latex || session?.question?.text || ''

/**
 * Milliseconds for a Firestore timestamp, tolerating the plain `{seconds}`
 * shape that arrives when a document is read straight after a write.
 * @param {*} timestamp - Firestore Timestamp or timestamp-like value.
 * @return {number} Epoch milliseconds, or 0 when unavailable.
 */
export const explainTimestampMillis = (timestamp) => {
  if (!timestamp) return 0
  if (typeof timestamp.toMillis === 'function') return timestamp.toMillis()
  if (typeof timestamp.seconds === 'number') return timestamp.seconds * 1000
  return 0
}

export const formatExplainTimestamp = (timestamp) => {
  const millis = explainTimestampMillis(timestamp)
  if (!millis) return ''
  return new Date(millis).toLocaleString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  })
}

export const formatExplainDate = (timestamp) => {
  const millis = explainTimestampMillis(timestamp)
  if (!millis) return ''
  return new Date(millis).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  })
}
